import React, { useState, useEffect } from "react";
import { withStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import {
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Switch,
  Grid,
} from "@material-ui/core";
import AddIcon from "@mui/icons-material/Add";
import styles from "./components.module.scss";
import DeleteIcon from "../../Assets/DeleteIcon-red.svg";

// Reusable FormField Component
const FormField = ({ label, children, className = "" }) => (
  <div className={`${styles.formField} ${className}`}>
    {label && <label className={styles.formLabel}>{label}</label>}
    {children}
  </div>
);

// Reusable DropdownOptionRow Component
const DropdownOptionRow = ({
  option,
  index,
  onChange,
  onDelete,
  canDelete,
}) => (
  <div className={styles.dropdownOptionRow}>
    <div className={styles.optionLabel}>{String.fromCharCode(65 + index)})</div>
    <StyledTextField
      variant="outlined"
      placeholder={`Option ${String.fromCharCode(65 + index)}`}
      value={option.value}
      onChange={(e) => onChange(option.id, e.target.value)}
      className={styles.optionInput}
      inputProps={{ maxLength: 50 }}
    />
    {canDelete && (
      <IconButton
        size="small"
        onClick={() => onDelete(option.id)}
        className={styles.deleteOptionButton}
      >
        <img src={DeleteIcon} alt="Delete" style={{ width: 20, height: 20 }} />
      </IconButton>
    )}
  </div>
);

// Attribute Type Options Configuration
const ATTRIBUTE_TYPE_OPTIONS = [
  { value: "dropdown", label: "Dropdown" },
  { value: "free_text", label: "Free Text" },
  { value: "single_image", label: "Single Image" },
  { value: "multiple_image", label: "Multiple Image" },
];

// Reusable TextField Props
const commonTextFieldProps = {
  variant: "outlined",
};

// Maximum number of dropdown options allowed
const MAX_DROPDOWN_OPTIONS = 6;

const dialogTitleStyles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(3.25),
    paddingBottom: theme.spacing(3),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: "18px",
    color: "#2E2E2E",
  },
});

const DialogTitle = withStyles(dialogTitleStyles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6" className={styles.modalTitle}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(3.25),
    paddingTop: theme.spacing(0),
    borderBottom: "unset",
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(3.25),
    // paddingTop: theme.spacing(2),
    // borderTop: "1px solid #E8E8E8",
    justifyContent: "space-between",
  },
}))(MuiDialogActions);

const StyledSwitch = withStyles((theme) => ({
  switchBase: {
    color: "#ffffff",
    "&$checked": {
      color: "#1c4980",
    },
    "&$checked + $track": {
      backgroundColor: "#1c4980",
    },
  },
  checked: {},
  track: {
    backgroundColor: "#9F9F9F",
  },
}))(Switch);

const StyledRadio = withStyles((theme) => ({
  root: {
    color: "#E4E4E4",
    paddingLeft: 0,
    "&$checked": {
      color: "#1c4980",
    },
  },
  checked: {},
}))(Radio);

const StyledFormControlLabel = withStyles((theme) => ({
  root: {
    margin: 0,
  },
  label: {
    fontSize: "14px",
    color: "#2e2e2e",
    fontWeight: 400,
  },
}))(FormControlLabel);

const StyledTextField = withStyles((theme) => ({
  root: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      "& fieldset": {
        borderColor: "#E4E4E4",
        borderWidth: "0.8px",
      },
      "&:hover fieldset": {
        borderColor: "#E4E4E4",
        borderWidth: "0.8px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#1c4980",
        borderWidth: "1px",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "14px 16px",
      fontSize: "14px",
      "&::placeholder": {
        color: "#757575",
        opacity: 1,
      },
    },
  },
}))(TextField);

const AddAttributeModal = ({
  open,
  onClose,
  onSave,
  attributeType = "checkin",
  editingAttribute = null,
}) => {
  const [attributeName, setAttributeName] = useState("");
  const [attributeTypeValue, setAttributeTypeValue] = useState("dropdown");
  const [dropdownOptions, setDropdownOptions] = useState([
    { id: 1, value: "" },
    { id: 2, value: "" },
  ]);
  const [isRequired, setIsRequired] = useState(true);

  useEffect(() => {
    if (editingAttribute) {
      setAttributeName(editingAttribute.attributeName || "");
      setAttributeTypeValue(editingAttribute.attributeType || "dropdown");
      setIsRequired(editingAttribute.isRequired ? true : false);
      if (
        editingAttribute.dropdownOptions &&
        editingAttribute.dropdownOptions.length > 0
      ) {
        setDropdownOptions(
          editingAttribute.dropdownOptions.map((opt, idx) => ({
            id: idx + 1,
            value: opt,
          }))
        );
      }
    } else {
      resetForm();
    }
  }, [editingAttribute, open]);

  const resetForm = () => {
    setAttributeName("");
    setAttributeTypeValue("dropdown");
    setDropdownOptions([
      { id: 1, value: "" },
      { id: 2, value: "" },
    ]);
    setIsRequired(true);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAddOption = () => {
    if (dropdownOptions.length >= MAX_DROPDOWN_OPTIONS) {
      return;
    }
    const newId =
      dropdownOptions.length > 0
        ? Math.max(...dropdownOptions.map((opt) => opt.id)) + 1
        : 1;
    setDropdownOptions([...dropdownOptions, { id: newId, value: "" }]);
  };

  const handleDeleteOption = (id) => {
    if (dropdownOptions.length > 1) {
      setDropdownOptions(dropdownOptions.filter((opt) => opt.id !== id));
    }
  };

  const handleOptionChange = (id, value) => {
    setDropdownOptions(
      dropdownOptions.map((opt) => (opt.id === id ? { ...opt, value } : opt))
    );
  };

  const handleSave = () => {
    const attributeData = {
      attributeName: attributeName.trim(),
      attributeType: attributeTypeValue,
      isRequired: isRequired ? 1 : 0,
    };

    if (attributeTypeValue === "dropdown") {
      const filteredOptions = dropdownOptions
        .map((opt) => opt.value.trim())
        .filter((v) => v);

      if (filteredOptions.length === 0) {
        return;
      }

      attributeData.dropdownOptions = filteredOptions;
    }

    if (!attributeData.attributeName || !attributeData.attributeType) {
      return;
    }

    onSave(attributeData, attributeType);
    handleClose();
  };

  const isSaveDisabled =
    !attributeName.trim() ||
    (attributeTypeValue === "dropdown" &&
      dropdownOptions.filter((opt) => opt.value.trim()).length === 0);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      classes={{
        paper: styles.modalPaper,
      }}
    >
      <DialogTitle onClose={handleClose}>Add New Attributes</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Attribute Name Field */}
          <Grid item xs={12}>
            <FormField label="Attribute Name">
              <StyledTextField
                {...commonTextFieldProps}
                fullWidth
                placeholder="Add Attribute.."
                value={attributeName}
                onChange={(e) => setAttributeName(e.target.value)}
              />
            </FormField>
          </Grid>

          {/* Attribute Type Field */}
          <Grid item xs={12}>
            <FormField label="Attribute Type">
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={attributeTypeValue}
                  onChange={(e) => setAttributeTypeValue(e.target.value)}
                  className={styles.radioGroup}
                >
                  {ATTRIBUTE_TYPE_OPTIONS.map((option) => (
                    <StyledFormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<StyledRadio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </FormField>
          </Grid>

          {/* Dropdown Options Field */}
          {attributeTypeValue === "dropdown" && (
            <Grid item xs={12}>
              <FormField>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <label className={styles.formLabel}>Dropdown Option</label>
                  {dropdownOptions.length < MAX_DROPDOWN_OPTIONS && (
                    <Button
                      startIcon={<AddIcon style={{ color: "#2E2E2E" }} />}
                      onClick={handleAddOption}
                      className={styles.addOptionButton}
                    >
                      Add Option
                    </Button>
                  )}
                </div>
                <div className={styles.dropdownOptionsContainer}>
                  {dropdownOptions.map((option, index) => (
                    <DropdownOptionRow
                      key={option.id}
                      option={option}
                      index={index}
                      onChange={handleOptionChange}
                      onDelete={handleDeleteOption}
                      canDelete={dropdownOptions.length > 2}
                    />
                  ))}
                </div>
              </FormField>
            </Grid>
          )}

          {/* Requirement Field */}
          <Grid item xs={12}>
            <FormField>
              <div className={styles.requirementSection}>
                <div className={styles.requirementLabelContainer}>
                  <label className={styles.formLabel}>Requirement</label>
                  <span className={styles.requirementDescription}>
                    This field is optional
                  </span>
                </div>
                <StyledSwitch
                  checked={!isRequired}
                  onChange={(e) => setIsRequired(!e.target.checked)}
                />
              </div>
            </FormField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={resetForm}
          className={styles.clearButton}
          variant="outlined"
        >
          Clear
        </Button>
        <Button
          onClick={handleSave}
          className={styles.saveButton}
          variant="contained"
          disabled={isSaveDisabled}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAttributeModal;
